import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Container,
  FormFeedback,
  Input,
  Label,
  Form,
  TabContent,
  TabPane,
  Modal,
  ModalHeader,
  ModalBody
} from "reactstrap";
import { Link, useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import axios from "axios";
import { API } from "../../api/urls";
import defaultLogo from "../../assets/images/tahsilat360_logo2.png";
import classnames from "classnames";

const ForgetPasswordPage = () => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const token = params.get("token");
  const companyCodeParam = params.get("company");
  const accountCodeParam = params.get("account");
  const initialTab = params.get("type");
  const isResetMode = Boolean(
      token && (companyCodeParam || accountCodeParam)
  );
  const onlyDigits = v => v.replace(/\D/g, "");

  // Tab state - default to account (matching login page)
  const [activeTab, setActiveTab] = useState(
      initialTab === "company" ? "company" : "account"
  ); // "company" or "account"

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Password visibility
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Company logo state
  const [companyLogo, setCompanyLogo] = useState(null);
  const [logoLoading, setLogoLoading] = useState(true);
  const [companyId, setCompanyId] = useState(null);
  const [companyCode, setCompanyCode] = useState("");

  const CONTRACT_ENDPOINT = {
    CANCELLATION: API.contract_cancel_return_privacy,  // /api/companies/contract/cancellation-return-privacy
    KVKK        : API.contract_personal_data,         // /api/companies/contract/personal-data-policy
    SERVICE     : API.contract_service_agreement      // /api/companies/contract/service-agreement
  };

  const [contractModal, setContractModal] = useState({
    open : false,
    title: "",
    html : "",
    busy : false
  });

  const [contactModal, setContactModal] = useState({
    open : false,
    busy : false,
    data : null,   // CompanyPublicInfoDTO
    error: null
  });

  const openContractModal = async (type) => {
    setContractModal(prev => ({ ...prev, open: true, busy: true, html: "", title: "" }));
    try {
      const urlFunc = CONTRACT_ENDPOINT[type];
      const url     = urlFunc(companyId);
      const res     = await axios.get(url); // public → token gerekmiyor

      if (res.data.statusCode !== 200 || !res.data.data)
        throw new Error(res.data.message || "Sözleşme bulunamadı");

      const titles = {
        CANCELLATION: "İptal / İade Koşulları ve Gizlilik",
        KVKK        : "Kişisel Veriler Politikası",
        SERVICE     : "Hizmet Sözleşmesi"
      };

      setContractModal({
        open : true,
        busy : false,
        html : res.data.data,
        title: titles[type]
      });
    } catch (err) {
      setContractModal({
        open : true,
        busy : false,
        html : `<p class="text-danger">Sözleşme bulunamadı.</p>`,
        title: "Uyarı"
      });
      console.error("Contract fetch error:", err);
    }
  };

  const openContactModal = async () => {
    setContactModal({ open: true, busy: true, data: null, error: null });
    try {
      const res = await axios.get(API.get_company_info_by_domain); // public → token gerekmiyor
      if (res.data.statusCode !== 200 || !res.data.data)
        throw new Error(res.data.message || "Bilgi bulunamadı");

      setContactModal({
        open : true,
        busy : false,
        data : res.data.data,
        error: null
      });
    } catch (err) {
      console.error("Contact fetch error:", err);
      setContactModal({
        open : true,
        busy : false,
        data : null,
        error: "İletişim bilgileri alınamadı"
      });
    }
  };

  // Login sayfasıyla birebir aynı modern tasarım stilleri
  const customStyles = `
  /* Font: uygulama genelindeki global font miras alınır (override yok) */
  .login-modern {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    position: relative;
    background: linear-gradient(135deg, #f6f8fc 0%, #eef1f8 100%);
    overflow: hidden;
  }
  .login-modern::before,
  .login-modern::after {
    content: "";
    position: absolute;
    border-radius: 50%;
    filter: blur(70px);
    opacity: 0.35;
    z-index: 0;
    pointer-events: none;
  }
  .login-modern::before { width: 440px; height: 440px; top: -130px; right: -90px; background: rgba(229,106,34,0.18); }
  .login-modern::after  { width: 400px; height: 400px; bottom: -140px; left: -110px; background: rgba(34,28,93,0.14); }

  .login-scroll {
    position: relative;
    z-index: 1;
    flex: 1;
    overflow-y: auto;
    display: flex;
    align-items: center;
  }

  .login-card {
    width: 100%;
    max-width: 980px;
    margin: 2rem auto;
    display: grid;
    grid-template-columns: 1.05fr 1fr;
    background: #ffffff;
    border: 1px solid #e9edf5;
    border-radius: 24px;
    box-shadow: 0 24px 60px rgba(34,28,93,0.12);
    overflow: hidden;
  }

  /* Brand panel (açık tema, beyaz ağırlıklı) */
  .login-brand {
    position: relative;
    padding: 2.75rem 2.5rem;
    color: #1f2a44;
    background: linear-gradient(160deg, #fbfcff 0%, #eef1fb 100%);
    border-right: 1px solid #e9edf5;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    overflow: hidden;
  }
  .login-brand::after {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(620px 320px at 115% -15%, rgba(229,106,34,0.12) 0%, transparent 60%);
    pointer-events: none;
  }
  .login-brand > * { position: relative; z-index: 1; }
  .brand-badge {
    display: inline-flex;
    align-items: center;
    gap: .5rem;
    padding: .42rem .85rem;
    background: rgba(34,28,93,0.06);
    border: 1px solid rgba(34,28,93,0.15);
    color: #221c5d;
    border-radius: 999px;
    font-size: .8rem;
    font-weight: 500;
    width: max-content;
  }
  .login-brand h2 {
    font-weight: 700;
    font-size: clamp(1.45rem, 2.4vw, 2.05rem);
    line-height: 1.25;
    margin: 1.5rem 0 .75rem;
    color: #221c5d;
  }
  .login-brand p.lead { color: #64748b; font-size: .95rem; margin-bottom: 0; }
  .brand-feature { display: flex; align-items: flex-start; gap: .8rem; margin-bottom: .95rem; }
  .brand-feature .bf-icon {
    flex-shrink: 0;
    width: 40px; height: 40px;
    border-radius: 12px;
    display: flex; align-items: center; justify-content: center;
    background: rgba(229,106,34,0.12);
    color: #e56a22;
    font-size: 1.2rem;
    border: 1px solid rgba(229,106,34,0.25);
  }
  .brand-feature .bf-title { font-weight: 600; font-size: .92rem; color: #1f2a44; }
  .brand-feature .bf-sub { color: #64748b; font-size: .8rem; }

  /* Form side */
  .login-form-side { padding: 2.5rem 2.5rem; background: #fff; }
  .login-modern .login-logo { height: clamp(60px, 9vw, 92px); width: auto; max-width: 100%; object-fit: contain; }
  .login-modern h5.welcome { font-weight: 700; color: #221c5d; }
  .login-modern .welcome-sub { color: #64748b; font-size: .9rem; }

  /* Segmented tabs */
  .seg-tabs {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: .35rem;
    background: #f1f3f9;
    border-radius: 14px;
    padding: .35rem;
    border: 1px solid #e6e9f2;
  }
  .seg-tabs .seg-tab {
    border: none;
    background: transparent;
    border-radius: 11px;
    padding: .62rem .5rem;
    font-weight: 600;
    font-size: .9rem;
    color: #64748b;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: .4rem;
    transition: color .2s ease, background .2s ease, box-shadow .2s ease;
  }
  .seg-tabs .seg-tab:hover { color: #334155; }
  .seg-tabs .seg-tab.account.active { background: #e56a22; color: #fff; box-shadow: 0 6px 16px rgba(229,106,34,0.35); }
  .seg-tabs .seg-tab.company.active { background: #221c5d; color: #fff; box-shadow: 0 6px 16px rgba(34,28,93,0.35); }

  /* Inputs */
  .login-modern .form-label { font-weight: 600; font-size: .82rem; color: #334155; margin-bottom: .3rem; }
  .login-modern .login-ig { position: relative; }
  .login-modern .login-ig .ig-icon {
    position: absolute;
    left: 14px;
    top: 24px;
    transform: translateY(-50%);
    color: #94a3b8;
    font-size: 1.15rem;
    pointer-events: none;
    z-index: 3;
  }
  .login-modern .login-ig .form-control {
    height: 48px;
    border-radius: 12px;
    border: 1.5px solid #e2e8f0;
    background: #f8fafc;
    padding-left: 42px;
    padding-right: 44px;
    font-size: .95rem;
    transition: border-color .2s, box-shadow .2s, background .2s;
  }
  .login-modern .login-ig .form-control:focus {
    border-color: #221c5d;
    background: #fff;
    box-shadow: 0 0 0 4px rgba(34,28,93,0.12);
  }
  .login-modern.tab-account .login-ig .form-control:focus {
    border-color: #e56a22;
    box-shadow: 0 0 0 4px rgba(229,106,34,0.15);
  }
  .login-modern .login-ig .form-control.is-invalid {
    border-color: #dc3545;
    background: #fff;
    background-image: none;
  }
  .login-modern .login-ig .pass-toggle {
    position: absolute;
    right: 8px;
    top: 24px;
    transform: translateY(-50%);
    background: transparent;
    border: none;
    color: #94a3b8;
    padding: .35rem .5rem;
    z-index: 3;
    cursor: pointer;
    line-height: 1;
  }
  .login-modern .login-ig .pass-toggle:hover { color: #334155; }
  .login-modern .invalid-feedback { display: block; font-size: .78rem; margin-top: .3rem; }

  /* Button */
  .login-modern .btn-login {
    width: 100%;
    height: 50px;
    border: none;
    border-radius: 12px;
    font-weight: 600;
    font-size: 1rem;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: .5rem;
    transition: transform .15s ease, box-shadow .2s ease, filter .2s ease;
  }
  .login-modern .btn-login:disabled { opacity: .7; cursor: not-allowed; }
  .login-modern .btn-login.account { background: linear-gradient(135deg, #e56a22 0%, #f0823a 100%); box-shadow: 0 10px 24px rgba(229,106,34,0.35); }
  .login-modern .btn-login.company { background: linear-gradient(135deg, #221c5d 0%, #322a86 100%); box-shadow: 0 10px 24px rgba(34,28,93,0.35); }
  .login-modern .btn-login:not(:disabled):hover { transform: translateY(-2px); filter: brightness(1.05); }
  .login-modern .btn-login:not(:disabled):active { transform: translateY(0); }

  .login-modern .register-cta { color: #221c5d; font-weight: 600; text-decoration: none; }
  .login-modern .register-cta:hover { text-decoration: underline; }

  .login-modern .tab-content { animation: lgFade .35s ease-out; }
  @keyframes lgFade { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }

  /* Footer (beyaz) */
  .auth-footer-modern {
    position: relative;
    z-index: 1;
    background: #ffffff;
    border-top: 1px solid #e9edf5;
  }
  .auth-footer-modern .footer-links { gap: clamp(.3rem, 1vw, .75rem); font-size: clamp(.68rem, .9vw, .82rem); }
  .auth-footer-modern .footer-links span { cursor: pointer; color: #64748b; transition: color .2s; }
  .auth-footer-modern .footer-links span:hover { color: #221c5d; }
  .auth-footer-modern .copyright, .auth-footer-modern .copyright a { color: #94a3b8; }

  /* Responsive */
  @media (max-width: 991.98px) {
    .login-brand { display: none; }
    .login-card { grid-template-columns: 1fr; max-width: 460px; }
    .login-form-side { padding: 2rem 1.5rem; }
  }
  @media (max-width: 575.98px) {
    .login-card { margin: 1rem auto; border-radius: 18px; }
    .login-form-side { padding: 1.5rem 1.15rem; }
    .auth-footer-modern .footer-links { font-size: .7rem; }
  }
  @media (prefers-reduced-motion: reduce) {
    .login-modern *, .login-modern *::before, .login-modern *::after { animation: none !important; transition: none !important; }
    .login-modern .btn-login:hover { transform: none; }
  }
  `;

  useEffect(() => {
    document.title = isResetMode
        ? "Şifreyi Sıfırla | Tahsilat360"
        : "Şifre Sıfırlama Talebi | Tahsilat360";
  }, [isResetMode]);

  // Fetch company logo
  useEffect(() => {
    const fetchCompanyLogo = async () => {
      setLogoLoading(true);
      try {
        const logoResponse = await axios.get(API.get_company_logo, {
          responseType: 'blob'
        });

        if (logoResponse.data && logoResponse.data.size > 0) {
          const logoUrl = URL.createObjectURL(logoResponse.data);
          setCompanyLogo(logoUrl);
        } else {
          setCompanyLogo(defaultLogo);
        }
      } catch (err) {
        console.error("Logo fetch error:", err);
        setCompanyLogo(defaultLogo);
      } finally {
        setLogoLoading(false);
      }
    };

    fetchCompanyLogo();
  }, []);

  // Tab toggle function
  const toggleTab = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
      setError("");
      setSuccess("");
      setCompanyCode("");
      requestForm.resetForm();
    }
  };

  // Talep oluşturma formu
  const requestForm = useFormik({
    initialValues: {
      email: "",
      companyCode: "",
      accountCode: ""
    },
    validationSchema: Yup.object({
      companyCode: activeTab === "company"
          ? Yup.string()
              .matches(/^\d+$/, "Sadece rakam giriniz")
              .required("Firma kodu zorunlu")
          : Yup.string(),
      accountCode: activeTab === "account"
          ? Yup.string()
              .matches(/^\d+$/, "Sadece rakam giriniz")
              .required("Cari/Bayi kodu zorunlu")
          : Yup.string(),
      email: Yup.string().email("Geçerli e-posta").required("E-posta zorunlu"),
    }),
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      setError("");
      setSuccess("");
      try {
        const params = {
          email: values.email
        };

        if (activeTab === "company") {
          params.companyCode = values.companyCode;
        } else {
          params.accountCode = values.accountCode;
        }

        const res = await axios.post(
            API.requestReset,
            null,
            { params }
        );
        setSuccess(res.data.message || "E-posta gönderildi. Gelen kutunuzu kontrol edin.");
        resetForm();
      } catch (err) {
        setError(err.response?.data?.message || "Bir hata oluştu.");
      } finally {
        setSubmitting(false);
      }
    }
  });

  // Şifre sıfırlama formu
  const resetForm = useFormik({
    initialValues: { newPassword: "", confirm: "" },
    validationSchema: Yup.object({
      newPassword: Yup.string().min(6, "En az 6 karakter").required("Yeni şifre zorunlu"),
      confirm: Yup.string()
          .oneOf([Yup.ref("newPassword")], "Şifreler eşleşmiyor")
          .required("Şifre onayı zorunlu"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      setError("");
      setSuccess("");

      try {
        const payload = {
          token,
          newPassword: values.newPassword,
        };

        if (companyCodeParam) {
          payload.companyCode = companyCodeParam;
        }

        if (accountCodeParam) {
          payload.accountCode = accountCodeParam;
        }

        const res = await axios.post(API.resetPassword, payload);

        setSuccess(res.data.message || "Şifre başarıyla değiştirildi.");
        setTimeout(() => navigate("/login"), 2000);
      } catch (err) {
        setError(err.response?.data?.message || "Bir hata oluştu.");
      } finally {
        setSubmitting(false);
      }
    }
  });

  const isAccount = activeTab === "account";

  return (
      <React.Fragment>
        <style>{customStyles}</style>
        <div className={`login-modern tab-${activeTab}`}>
          <div className="login-scroll px-3 px-sm-4">
            <div className="login-card">

              {/* Sol marka paneli (lg ve üzeri) */}
              <div className="login-brand">
                <div>
                  <span className="brand-badge">
                    <i className="mdi mdi-lock-reset"></i> Güvenli Şifre Sıfırlama
                  </span>
                  <h2>Hesabınıza erişimi güvenle geri kazanın.</h2>
                  <p className="lead">
                    Hesap tipinizi seçin, kodunuzu ve e-postanızı girin; sıfırlama bağlantısını size gönderelim.
                  </p>
                </div>

                <div className="mt-4">
                  <div className="brand-feature">
                    <div className="bf-icon"><i className="mdi mdi-email-fast-outline"></i></div>
                    <div>
                      <div className="bf-title">E-posta ile Sıfırlama</div>
                      <div className="bf-sub">Sıfırlama bağlantısı kayıtlı e-postanıza gönderilir</div>
                    </div>
                  </div>
                  <div className="brand-feature">
                    <div className="bf-icon"><i className="mdi mdi-shield-lock-outline"></i></div>
                    <div>
                      <div className="bf-title">Güvenli &amp; Zaman Sınırlı</div>
                      <div className="bf-sub">Bağlantı belirli süre sonra otomatik geçersiz olur</div>
                    </div>
                  </div>
                  <div className="brand-feature">
                    <div className="bf-icon"><i className="mdi mdi-account-check-outline"></i></div>
                    <div>
                      <div className="bf-title">Cari &amp; Firma Desteği</div>
                      <div className="bf-sub">Hem cari/bayi hem firma hesapları için geçerli</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sağ form paneli */}
              <div className="login-form-side">
                <div className="text-center mb-3">
                  <Link to="/dashboard" className="d-inline-block auth-logo">
                    <img
                        src={companyLogo || defaultLogo}
                        alt="Logo"
                        className="login-logo img-fluid"
                        onError={() => companyLogo !== defaultLogo && setCompanyLogo(defaultLogo)}
                    />
                  </Link>
                </div>

                <div className="text-center mb-4">
                  {isResetMode ? (
                      <>
                        <h5 className="welcome mb-1">Yeni Şifre Belirle</h5>
                        <p className="welcome-sub mb-0">Yeni şifrenizi girin ve onaylayın.</p>
                      </>
                  ) : (
                      <>
                        <h5 className="welcome mb-1">Şifremi Unuttum</h5>
                        <p className="welcome-sub mb-0">Hesap tipinizi seçin ve şifre sıfırlama talebi oluşturun.</p>
                      </>
                  )}
                </div>

                {error && (
                    <div className="alert alert-danger mb-3 d-flex align-items-center gap-2" role="alert">
                      <i className="mdi mdi-alert-circle-outline"></i>
                      <span>{error}</span>
                    </div>
                )}
                {success && (
                    <div className="alert alert-success mb-3 d-flex align-items-center gap-2" role="alert">
                      <i className="mdi mdi-check-circle-outline"></i>
                      <span>{success}</span>
                    </div>
                )}

                {!isResetMode ? (
                    <>
                      {/* Segmented tab kontrolü */}
                      <div className="seg-tabs mb-4" role="tablist">
                        <button
                            type="button"
                            role="tab"
                            aria-selected={isAccount}
                            className={classnames("seg-tab account", { active: isAccount })}
                            onClick={() => toggleTab("account")}
                        >
                          <i className="mdi mdi-account-outline"></i>
                          Cari/Bayi Giriş
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={!isAccount}
                            className={classnames("seg-tab company", { active: !isAccount })}
                            onClick={() => toggleTab("company")}
                        >
                          <i className="mdi mdi-office-building-outline"></i>
                          Firma Giriş
                        </button>
                      </div>

                      <TabContent activeTab={activeTab} key={activeTab}>
                        <TabPane tabId="account">
                          <Form
                              className="custom-form"
                              onSubmit={(e) => { e.preventDefault(); requestForm.handleSubmit(); return false; }}
                          >
                            <div className="mb-3">
                              <Label className="form-label" for="fp_accountCode">Cari/Bayi Kodu</Label>
                              <div className="login-ig">
                                <i className="mdi mdi-account-outline ig-icon"></i>
                                <Input
                                    id="fp_accountCode"
                                    name="accountCode"
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    placeholder="Cari Kodunu Girin"
                                    value={requestForm.values.accountCode}
                                    onChange={e => {
                                      const digits = onlyDigits(e.target.value);
                                      requestForm.setFieldValue("accountCode", digits);
                                    }}
                                    onBlur={requestForm.handleBlur}
                                    invalid={!!(requestForm.touched.accountCode && requestForm.errors.accountCode)}
                                />
                                {requestForm.touched.accountCode && requestForm.errors.accountCode && (
                                    <FormFeedback>{requestForm.errors.accountCode}</FormFeedback>
                                )}
                              </div>
                            </div>

                            <div className="mb-3">
                              <Label className="form-label" for="fp_email_a">E-posta</Label>
                              <div className="login-ig">
                                <i className="mdi mdi-email-outline ig-icon"></i>
                                <Input
                                    id="fp_email_a"
                                    name="email"
                                    type="email"
                                    placeholder="E-posta Adresinizi Girin"
                                    onChange={requestForm.handleChange}
                                    onBlur={requestForm.handleBlur}
                                    value={requestForm.values.email}
                                    invalid={!!(requestForm.touched.email && requestForm.errors.email)}
                                />
                                {requestForm.touched.email && requestForm.errors.email && (
                                    <FormFeedback>{requestForm.errors.email}</FormFeedback>
                                )}
                              </div>
                            </div>

                            <div className="mt-4">
                              <button className="btn-login account" type="submit" disabled={requestForm.isSubmitting}>
                                {requestForm.isSubmitting ? "Gönderiliyor..." : "Şifre Sıfırlama Talebi"}
                                <i className="mdi mdi-arrow-right"></i>
                              </button>
                            </div>
                          </Form>
                        </TabPane>

                        <TabPane tabId="company">
                          <Form
                              className="custom-form"
                              onSubmit={(e) => { e.preventDefault(); requestForm.handleSubmit(); return false; }}
                          >
                            <div className="mb-3">
                              <Label className="form-label" for="fp_companyCode">Firma Kodu</Label>
                              <div className="login-ig">
                                <i className="mdi mdi-office-building-outline ig-icon"></i>
                                <Input
                                    id="fp_companyCode"
                                    name="companyCode"
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    placeholder="Firma Kodunu Girin"
                                    value={requestForm.values.companyCode}
                                    onChange={e => {
                                      const digits = onlyDigits(e.target.value);
                                      requestForm.setFieldValue("companyCode", digits);
                                      setCompanyCode(digits);
                                    }}
                                    onBlur={requestForm.handleBlur}
                                    invalid={!!(requestForm.touched.companyCode && requestForm.errors.companyCode)}
                                />
                                {requestForm.touched.companyCode && requestForm.errors.companyCode && (
                                    <FormFeedback>{requestForm.errors.companyCode}</FormFeedback>
                                )}
                              </div>
                            </div>

                            <div className="mb-3">
                              <Label className="form-label" for="fp_email_c">E-posta</Label>
                              <div className="login-ig">
                                <i className="mdi mdi-email-outline ig-icon"></i>
                                <Input
                                    id="fp_email_c"
                                    name="email"
                                    type="email"
                                    placeholder="E-posta Adresinizi Girin"
                                    onChange={requestForm.handleChange}
                                    onBlur={requestForm.handleBlur}
                                    value={requestForm.values.email}
                                    invalid={!!(requestForm.touched.email && requestForm.errors.email)}
                                />
                                {requestForm.touched.email && requestForm.errors.email && (
                                    <FormFeedback>{requestForm.errors.email}</FormFeedback>
                                )}
                              </div>
                            </div>

                            <div className="mt-4">
                              <button className="btn-login company" type="submit" disabled={requestForm.isSubmitting}>
                                {requestForm.isSubmitting ? "Gönderiliyor..." : "Şifre Sıfırlama Talebi"}
                                <i className="mdi mdi-arrow-right"></i>
                              </button>
                            </div>
                          </Form>
                        </TabPane>
                      </TabContent>
                    </>
                ) : (
                    <Form
                        className="custom-form"
                        onSubmit={(e) => { e.preventDefault(); resetForm.handleSubmit(); return false; }}
                    >
                      <div className="mb-3">
                        <Label className="form-label" for="fp_newPassword">Yeni Şifre</Label>
                        <div className="login-ig">
                          <i className="mdi mdi-lock-outline ig-icon"></i>
                          <Input
                              id="fp_newPassword"
                              name="newPassword"
                              type={showNew ? "text" : "password"}
                              placeholder="Yeni şifrenizi giriniz"
                              autoComplete="new-password"
                              onChange={resetForm.handleChange}
                              onBlur={resetForm.handleBlur}
                              value={resetForm.values.newPassword}
                              invalid={!!(resetForm.touched.newPassword && resetForm.errors.newPassword)}
                          />
                          <button
                              type="button"
                              className="pass-toggle"
                              aria-label={showNew ? "Şifreyi gizle" : "Şifreyi göster"}
                              onClick={() => setShowNew(prev => !prev)}
                          >
                            <i className={showNew ? "mdi mdi-eye-outline" : "mdi mdi-eye-off-outline"}></i>
                          </button>
                          {resetForm.touched.newPassword && resetForm.errors.newPassword && (
                              <FormFeedback>{resetForm.errors.newPassword}</FormFeedback>
                          )}
                        </div>
                      </div>

                      <div className="mb-3">
                        <Label className="form-label" for="fp_confirm">Şifre Tekrar</Label>
                        <div className="login-ig">
                          <i className="mdi mdi-lock-check-outline ig-icon"></i>
                          <Input
                              id="fp_confirm"
                              name="confirm"
                              type={showConfirm ? "text" : "password"}
                              placeholder="Şifreyi tekrar giriniz"
                              autoComplete="new-password"
                              onChange={resetForm.handleChange}
                              onBlur={resetForm.handleBlur}
                              value={resetForm.values.confirm}
                              invalid={!!(resetForm.touched.confirm && resetForm.errors.confirm)}
                          />
                          <button
                              type="button"
                              className="pass-toggle"
                              aria-label={showConfirm ? "Şifreyi gizle" : "Şifreyi göster"}
                              onClick={() => setShowConfirm(prev => !prev)}
                          >
                            <i className={showConfirm ? "mdi mdi-eye-outline" : "mdi mdi-eye-off-outline"}></i>
                          </button>
                          {resetForm.touched.confirm && resetForm.errors.confirm && (
                              <FormFeedback>{resetForm.errors.confirm}</FormFeedback>
                          )}
                        </div>
                      </div>

                      <div className="mt-4">
                        <button className="btn-login company" type="submit" disabled={resetForm.isSubmitting}>
                          {resetForm.isSubmitting ? "Güncelleniyor..." : "Şifreyi Güncelle"}
                          <i className="mdi mdi-arrow-right"></i>
                        </button>
                      </div>
                    </Form>
                )}

                <div className="mt-4 text-center">
                  <p className="text-muted mb-0" style={{ fontSize: ".9rem" }}>
                    {!isResetMode ? (
                        <>Şifrenizi hatırladınız mı?{" "}
                          <Link to={`/login?type=${activeTab}`} className="register-cta">Giriş Yap</Link>
                        </>
                    ) : (
                        <>Yeni talep için{" "}
                          <Link to="/forgot-password" className="register-cta">buraya tıklayın</Link>
                        </>
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer (logosuz — sadece metin) */}
          <footer className="auth-footer-modern">
            <Container fluid className="px-3 px-sm-4">
              <Row className="align-items-center pt-2">
                <Col lg={12} className="text-center mb-2">
                  <div className="footer-links d-flex justify-content-center flex-wrap mb-2">
                    <span role="button" onClick={openContactModal}>İletişim</span>
                    <span role="button" onClick={() => openContractModal("CANCELLATION")}>İptal, İade Koşulları ve Gizlilik</span>
                    <span role="button" onClick={() => openContractModal("KVKK")}>Kişisel Veriler Politikası</span>
                    <span role="button" onClick={() => openContractModal("SERVICE")}>Hizmet Sözleşmesi</span>
                  </div>
                </Col>
              </Row>
              <Row className="border-top border-light border-opacity-25 pt-2 pb-2">
                <Col className="text-center copyright">
                  <p className="small mb-0">
                    <a href="https://tahsilat360.com/" target="_blank" rel="noopener noreferrer" className="text-decoration-none copyright">
                      © {new Date().getFullYear()} Tahsilat360 | Online Tahsilat Sistemi. Tüm hakları saklıdır.
                    </a>
                  </p>
                </Col>
              </Row>
            </Container>
          </footer>
        </div>

        {/* Sözleşme Modalı */}
        <Modal isOpen={contractModal.open} toggle={() => setContractModal(p => ({ ...p, open:false }))} size="lg">
          <ModalHeader toggle={() => setContractModal(p => ({ ...p, open:false }))}>
            {contractModal.title}
          </ModalHeader>
          <ModalBody style={{ maxHeight: "70vh", overflowY: "auto" }}>
            {contractModal.busy ? (
                <div className="text-center py-4">
                  <span className="spinner-border" />
                </div>
            ) : (
                <div
                    style={{ whiteSpace: "pre-wrap" }}
                    dangerouslySetInnerHTML={{ __html: contractModal.html }}
                />
            )}
          </ModalBody>
        </Modal>

        {/* İletişim Modalı */}
        <Modal
            isOpen={contactModal.open}
            toggle={() => setContactModal(p => ({ ...p, open:false }))}>
          <ModalHeader toggle={() => setContactModal(p => ({ ...p, open:false }))}>
            İletişim Bilgileri
          </ModalHeader>

          <ModalBody style={{ maxHeight:"70vh", overflowY:"auto" }}>
            {contactModal.busy && (
                <div className="text-center py-4">
                  <span className="spinner-border" />
                </div>
            )}

            {!contactModal.busy && contactModal.error && (
                <p className="text-danger">{contactModal.error}</p>
            )}

            {!contactModal.busy && contactModal.data && (
                <div style={{ whiteSpace:"pre-wrap" }}>
                  <strong>{contactModal.data.companyName}</strong>{"\n\n"}

                  {contactModal.data.phoneNumber && (
                      <>
                        <strong>Telefon:</strong> {contactModal.data.phoneNumber}{"\n"}
                      </>
                  )}

                  {contactModal.data.contactEmail && (
                      <>
                        <strong>E-posta:</strong> {contactModal.data.contactEmail}{"\n"}
                      </>
                  )}

                  {contactModal.data.address && (
                      <>
                        <strong>Adres:</strong>{"\n"}{contactModal.data.address}
                      </>
                  )}
                </div>
            )}
          </ModalBody>
        </Modal>
      </React.Fragment>
  );
};

ForgetPasswordPage.propTypes = {
  history: PropTypes.object,
};

export default ForgetPasswordPage;
